import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { BasicProviderProps, BlogContextType, BlogItem } from "../types";
import ErrorBoundary from "../components/error/ErrorBoundary";
import { DashboardContext } from "./DashboardContext";
import useDataClient from "../hooks/useDataClient";
import { AppContext } from "./AppContext";
import { blogAdapter } from "../adapter/blog.adapter";


export const BlogContext = createContext<BlogContextType | null>(null);

export default function BlogProvider({ children }: BasicProviderProps) {
    const appContext = useContext(AppContext)
    const dashboardContext = useContext(DashboardContext)
    const [editor, setEditor] = useState<boolean>(false)
    const [entryTarget, setEntryTarget] = useState<BlogItem | null>(null)
    const blogClient = useDataClient({ appContext })

    function addEntry(data: BlogItem) {
        blogClient.executeRequest(blogAdapter.addEntry(data))
    }

    function editEntry(data: BlogItem) {
        blogClient.executeRequest(blogAdapter.editEntry(data))
    }

    function deleteEntry(data: BlogItem) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea eliminar la entrada "${data.title}"? Esto no impactará la documentación remota, sin embargo, matendrá los comentarios generados en caso de existir.`,
            type: 'warning',
            onAccept: () => blogClient.executeRequest(blogAdapter.deleteEntry(data))
        })
    }

    function reloadResources(message: string) {
        dashboardContext?.updateAppData('blogEntries')
        appContext?.setAppDialog({ message: message, type: 'success', onClose: () => setEditor(false) })
    }

    const providerData = useMemo<BlogContextType>(() => ({
        editor,
        setEditor,
        entryTarget,
        setEntryTarget,
        entries: dashboardContext?.appData.blogEntries || [],
        addEntry,
        editEntry,
        deleteEntry
    }), [editor, entryTarget, dashboardContext?.appData.blogEntries])

    useEffect(() => {
        dashboardContext?.updateAppData('blogEntries')
    }, [])

    useEffect(() => {
        if (blogClient.status.data) {
            reloadResources(`Entradas de blog actualizadas`)
        }
    }, [blogClient.status.data])

    return (
        <ErrorBoundary>
            <BlogContext.Provider value={providerData}>
                {children}
            </BlogContext.Provider>
        </ErrorBoundary>
    )
}